import { useEffect, useState, type ReactNode } from 'react';
import { Box, Flex, IconButton, useBreakpointValue } from '@chakra-ui/react';
import { useRouterState } from '@tanstack/react-router';
import { LuMenu, LuPanelLeftClose, LuPanelLeftOpen } from 'react-icons/lu';
import {
  DrawerBackdrop,
  DrawerBody,
  DrawerContent,
  DrawerHeader,
  DrawerRoot,
  DrawerTitle,
} from '@/shared/ui/chakra/drawer';
import { Tooltip } from '@/shared/ui/chakra/tooltip';
import { ShellNavLink } from './ShellNavLink';
import { ShellTabBar, TAB_BAR_HEIGHT } from './ShellTabBar';
import type { ShellNavItem, ShellNavSection } from './types';

/* Layout-only shell shared by AppShell (admin) and ClientShell (provider cabinet) — permissions,
   chat badges, /map's "give the canvas every pixel" mode, install-prompt/account menus all stay
   in the two callers. This component only knows: here's a nav tree, here's a header, here's an
   optional curated subset for a bottom tab bar, render it responsively.

   Desktop (`lg`=62em=992px, `web/src/shared/theme/system.ts`) renders byte-for-byte what the two
   shells looked like before this refactor: a fixed aside, no drawer, no tab bar. Below `lg` the
   aside is replaced by a hamburger-triggered drawer over the same `sections`, plus an optional
   bottom tab bar when there's more than one thing worth surfacing there. */

export interface ResponsiveShellProps {
  brand: ReactNode;
  sidebarAriaLabel: string;
  sections: ShellNavSection[];
  /** Curated subset for the bottom tab bar. Fewer than 2 items and the bar simply isn't
      rendered — real conditional render, not a CSS hide, so e.g. ClientShell's single nav item
      never needs a shell-specific "no tab bar" flag. */
  tabItems?: ShellNavItem[];
  menuTabLabel: string;
  /** Omit both to get an always-expanded, non-collapsible sidebar (ClientShell today). Provide
      both for the collapsible icon-rail behaviour (AppShell today). */
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  collapseLabels?: { collapse: string; expand: string };
  /** The whole right-hand header row — shell-specific (NotificationsToggle, account menu, …). */
  headerContent: ReactNode;
  mobileMenuLabel: string;
  mobileMenuTitle: ReactNode;
  /** Same flag AppShell already used to drop desktop padding to `p="3"` on /map — on mobile it
      additionally hides the tab bar so a fullscreen-map-adjacent route gets the full viewport. */
  edgeToEdge?: boolean;
  children: ReactNode;
}

export function ResponsiveShell({
  brand,
  sidebarAriaLabel,
  sections,
  tabItems,
  menuTabLabel,
  collapsed = false,
  onToggleCollapse,
  collapseLabels,
  headerContent,
  mobileMenuLabel,
  mobileMenuTitle,
  edgeToEdge = false,
  children,
}: ResponsiveShellProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const isDesktop = useBreakpointValue({ base: false, lg: true });
  const showTabBar = (tabItems?.length ?? 0) >= 2 && !edgeToEdge;

  // Belt-and-suspenders alongside each ShellNavLink's own onNavigate: covers programmatic
  // navigation (e.g. a redirect) closing the drawer too, not just a direct tap.
  useEffect(() => {
    setDrawerOpen(false);
  }, [pathname]);

  // Resizing/rotating into the desktop breakpoint while the drawer happens to be open (DevTools
  // device-toolbar toggle, a foldable unfolding) shouldn't leave it open behind the now-visible
  // sidebar.
  useEffect(() => {
    if (isDesktop) setDrawerOpen(false);
  }, [isDesktop]);

  return (
    <Flex h="100dvh">
      <Box
        as="aside"
        aria-label={sidebarAriaLabel}
        display={{ base: 'none', lg: 'flex' }}
        w={collapsed ? '60px' : '260px'}
        flexShrink={0}
        borderRightWidth="1px"
        borderColor="border"
        bg="bg.panel"
        p={collapsed ? '2' : '4'}
        flexDirection="column"
        gap="6"
        overflowY="auto"
        overflowX="hidden"
        transition="width 0.15s ease"
      >
        {brand}
        <Box as="nav" flex="1" display="flex" flexDirection="column" gap="6">
          {sections.map((section) => (
            <Box key={section.key} display="flex" flexDirection="column" gap="1">
              {section.items.map((item) => (
                <ShellNavLink key={item.key} item={item} collapsed={collapsed} />
              ))}
            </Box>
          ))}
        </Box>
        {onToggleCollapse && collapseLabels && (
          <Flex justify={collapsed ? 'center' : 'flex-end'}>
            <Tooltip
              content={collapsed ? collapseLabels.expand : collapseLabels.collapse}
              positioning={{ placement: 'right' }}
            >
              <IconButton
                aria-label={collapsed ? collapseLabels.expand : collapseLabels.collapse}
                variant="ghost"
                size="sm"
                onClick={onToggleCollapse}
              >
                {collapsed ? <LuPanelLeftOpen /> : <LuPanelLeftClose />}
              </IconButton>
            </Tooltip>
          </Flex>
        )}
      </Box>

      <DrawerRoot open={drawerOpen} onOpenChange={(e) => setDrawerOpen(e.open)} placement="start">
        <DrawerBackdrop />
        <DrawerContent offset="0" maxW="280px">
          <DrawerHeader pt="max(1rem, env(safe-area-inset-top, 0px))">
            <DrawerTitle>{mobileMenuTitle}</DrawerTitle>
          </DrawerHeader>
          <DrawerBody
            pt="2"
            pb="max(1rem, env(safe-area-inset-bottom, 0px))"
            display="flex"
            flexDirection="column"
            gap="6"
          >
            {sections.map((section) => (
              <Box key={section.key} display="flex" flexDirection="column" gap="1">
                {section.items.map((item) => (
                  <ShellNavLink
                    key={item.key}
                    item={item}
                    collapsed={false}
                    onNavigate={() => setDrawerOpen(false)}
                  />
                ))}
              </Box>
            ))}
          </DrawerBody>
        </DrawerContent>
      </DrawerRoot>

      <Flex direction="column" flex="1" minW="0" minH="0" overflowX="hidden">
        <Flex
          as="header"
          align="center"
          gap="2"
          px={{ base: '3', lg: '6' }}
          pt="env(safe-area-inset-top, 0px)"
          minH="64px"
          flexShrink={0}
          borderBottomWidth="1px"
          borderColor="border"
          bg="bg.panel"
        >
          <IconButton
            aria-label={mobileMenuLabel}
            variant="ghost"
            size="sm"
            display={{ base: 'inline-flex', lg: 'none' }}
            onClick={() => setDrawerOpen(true)}
          >
            <LuMenu />
          </IconButton>
          <Flex flex="1" align="center" justify="flex-end" gap="3">
            {headerContent}
          </Flex>
        </Flex>

        <Box
          as="main"
          flex="1"
          minW="0"
          overflowY="auto"
          p={edgeToEdge ? '3' : { base: '2', sm: '3', lg: '6' }}
          pb={
            showTabBar
              ? { base: `calc(${TAB_BAR_HEIGHT} + env(safe-area-inset-bottom, 0px) + 0.5rem)` }
              : undefined
          }
          bg="bg.subtle"
        >
          {children}
        </Box>
      </Flex>

      {showTabBar && (
        <ShellTabBar
          items={tabItems ?? []}
          menuLabel={menuTabLabel}
          onOpenMenu={() => setDrawerOpen(true)}
        />
      )}
    </Flex>
  );
}
